/**
 * @whatItDoes This service provides and stores data about the current authenticated user.
 * Does not cover the actual authentication, as covered in AuthenticationService
 *
 * @howToUse
 * - Subscribe to getters ending in 'Events' to be informed of login and logout events
 * - Use the login() and logout() methods to cache and destroy AuthData (tokens)
 * - Cache the User object with currentUser()
 * - Cache and retrieve the current device installation
 */
import { Injectable, OnDestroy } from '@angular/core';
import { ApplicationException, CacheKeyConstants, isNullOrUndefined, Permission } from '@uzurv/core';
import { Deserialize, Serialize } from 'cerialize';
import { Observable, Subject } from 'rxjs';
import { RequiredPermissions } from '../../interfaces';
import { AuthenticatedUser } from '../../models/authenticated-user';
import { AuthData } from './auth-data';

@Injectable({
  providedIn: 'root'
})
export class AuthDataService implements OnDestroy {
  private loginEvent: Subject<boolean> = new Subject<boolean>();
  private loggedInUserDataStoredEvent: Subject<boolean> = new Subject<boolean>();
  private logoutEvent: Subject<boolean> = new Subject<boolean>();

  constructor() {}

  ngOnDestroy() {
    this.loginEvent.complete();
    this.loggedInUserDataStoredEvent.complete();
    this.logoutEvent.complete();
  }

  /**
   * Subscribe here to get login events
   */
  get loginEvents(): Observable<boolean> {
    return this.loginEvent.asObservable();
  }

  /**
   * Subscribe here to get notified when the user's data is cached
   */
  get loggedInUserDataStoredEvents(): Observable<boolean> {
    return this.loggedInUserDataStoredEvent.asObservable();
  }

  /**
   * Subscribe here to get logout events
   */
  get logoutEvents(): Observable<boolean> {
    return this.logoutEvent.asObservable();
  }

  /**
   * Returns if the current user is logged in by testing cached AuthData
   */
  get isLoggedIn(): boolean {
    return !isNullOrUndefined(this.currentUserAuthData);
  }

  /**
   * Returns current user's AuthData
   */
  get currentUserAuthData(): AuthData {
    if (!localStorage.getItem(CacheKeyConstants.CURRENT_USER_AUTH_DATA)) {
      return null;
    }

    return Deserialize(JSON.parse(localStorage.getItem(CacheKeyConstants.CURRENT_USER_AUTH_DATA)), AuthData);
  }

  /**
   * Returns the current user object from the cache
   */
  get currentUser(): AuthenticatedUser {
    if (!localStorage.getItem(CacheKeyConstants.CURRENT_USER)) {
      return null;
    }

    return new AuthenticatedUser(JSON.parse(localStorage.getItem(CacheKeyConstants.CURRENT_USER)));
  }

  /**
   * Caches the current user in localStorage
   *
   * @param currentUser a User object to be cached
   */
  set currentUser(currentUser: AuthenticatedUser) {
    localStorage.setItem(CacheKeyConstants.CURRENT_USER, JSON.stringify(currentUser.toJSON()));
    this.loggedInUserDataStoredEvent.next(true);
  }

  removeCurrentUserData() {
    localStorage.removeItem(CacheKeyConstants.CURRENT_USER_AUTH_DATA);
    localStorage.removeItem(CacheKeyConstants.CURRENT_USER);
    localStorage.removeItem(CacheKeyConstants.CURRENT_USER_ROLES);
    localStorage.removeItem(CacheKeyConstants.TNC_DRIVER_PROFILE);
  }

  removeCachedDomainData() {
    CacheKeyConstants.clearClearableValues();
  }

  login(authData: AuthData) {
    this.removeCachedDomainData();
    this.setCurrentUserAuthData(authData);
  }

  /**
   * Saves the current user's permissions
   *
   * @param permissions
   */
  setPermissions(permissions: Array<Permission>) {
    try {
      const currentUserAuthData = this.currentUserAuthData;
      currentUserAuthData.permissions = permissions;
      this.setCurrentUserAuthData(currentUserAuthData);
    } catch {
      throw new ApplicationException('There must be cached user authentication data in order to store user permissions');
    }
  }

  /**
   * Returns the current user's permissions
   */
  getPermissions(): Array<Permission> {
    try {
      const currentUserAuthData = this.currentUserAuthData;
      if (isNullOrUndefined(currentUserAuthData)) {
        return [];
      }
      return currentUserAuthData.permissions;
    } catch (err) {
      throw new ApplicationException('Could not get permissions');
    }
  }

  hasPermission(requiredPermissions: RequiredPermissions): boolean {
    const permissions = this.getPermissions().map(permission => permission.name);

    let isHasAnyPermissionMet = false;
    let isHasAllPermissionsMet = false;

    if (
      !requiredPermissions.any ||
      requiredPermissions.any.length === 0 ||
      requiredPermissions.any.some(permission => permissions.includes(permission))
    ) {
      isHasAnyPermissionMet = true;
    }

    if (
      !requiredPermissions.all ||
      requiredPermissions.all.length === 0 ||
      requiredPermissions.all.every(permission => permissions.includes(permission))
    ) {
      isHasAllPermissionsMet = true;
    }

    return isHasAllPermissionsMet && isHasAnyPermissionMet;
  }

  /**
   * Returns the current user's cached roles (athorites)
   */
  getRoles(): Array<string> {
    try {
      const currentUserAuthData = this.currentUserAuthData;
      if (isNullOrUndefined(currentUserAuthData)) {
        return [];
      }
      return currentUserAuthData.authorities;
    } catch (err) {
      throw new ApplicationException('Could not get roles');
    }
  }

  hasRole(roleName: string): boolean {
    return this.getRoles().includes(roleName);
  }

  sendUserLoggedInEvent() {
    this.loginEvent.next(true);
  }

  /**
   * Cleans login data cache and notifies listeners of a logout event
   */
  clearAuthData(): void {
    this.removeCurrentUserData();
    this.removeCachedDomainData();
    this.logoutEvent.next(true);
  }

  /**
   * Caches the AuthData object of an authenticated user
   *
   * @param currentUserAuthData an AuthData object to be cached
   */
  private setCurrentUserAuthData(authData: AuthData): void {
    localStorage.setItem(CacheKeyConstants.CURRENT_USER_AUTH_DATA, JSON.stringify(Serialize(authData)));
  }
}
